import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, ExternalLink, MapPin, Bed, Bath, Square, Trash2 } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { getUserFavorites, removeFromFavorites, getClientInfo, addManualFavorite } from "../supabase";

// Calcula el capital máximo de hipoteca dado un pago mensual máximo (misma lógica que en SQL)
const calculateMortgagePrincipalFromPayment = (monthlyPayment, annualRate = 0.035, years = 30) => {
  if (!monthlyPayment || monthlyPayment <= 0) return 0;
  if (!annualRate || annualRate <= 0) return monthlyPayment * years * 12;

  const r = annualRate / 12;
  const n = years * 12;

  return (
    (monthlyPayment * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n))
  );
};

// Calcula el housing score de una propiedad concreta para un usuario
const calculatePropertyHousingScore = (income, savings, debts, propertyPrice) => {
  if (
    income == null ||
    savings == null ||
    debts == null ||
    propertyPrice == null ||
    propertyPrice <= 0
  ) {
    return null;
  }

  const effectiveSavings = Math.max(savings - debts, 0);
  const maxPayment33 = Math.max(income * 0.33, 0);
  const loanCapacity33 = calculateMortgagePrincipalFromPayment(maxPayment33, 0.035, 30);
  const totalCapacity = effectiveSavings + loanCapacity33;

  if (totalCapacity <= 0) return 0;

  const rawScore = (100 * totalCapacity) / propertyPrice;
  // Limitar a 0-100 y redondear a 2 decimales para consistencia
  const bounded = Math.min(100, Math.max(0, rawScore));
  return Math.round(bounded * 100) / 100;
};

const Favorites = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualUrl, setManualUrl] = useState("");
  const [manualTitle, setManualTitle] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [savingManual, setSavingManual] = useState(false);
  const [manualError, setManualError] = useState("");

  const loadFavorites = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);

      // Cargar datos del cliente (income, savings, debts) y sus favoritos en paralelo
      const [client, userFavorites] = await Promise.all([
        getClientInfo(user.email),
        getUserFavorites(user.uid),
      ]);

      // Si el cliente tiene income, savings y debts, calculamos un score por propiedad
      let enrichedFavorites = userFavorites || [];
      if (
        client &&
        client.income != null &&
        client.savings != null &&
        client.debts != null
      ) {
        enrichedFavorites = enrichedFavorites.map((favorite) => ({
          ...favorite,
          property_housing_score: calculatePropertyHousingScore(
            client.income,
            client.savings,
            client.debts,
            favorite.property_price
          ),
        }));
      }

      setFavorites(enrichedFavorites);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user, loadFavorites]);

  const handleRemoveFavorite = async (propertyCode) => {
    try {
      await removeFromFavorites(user.uid, propertyCode);
      await loadFavorites();
    } catch (error) {
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'No especificado';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleAddManualFavorite = async (e) => {
    e.preventDefault();
    setManualError("");

    if (!user) return;

    const url = manualUrl.trim();
    const title = manualTitle.trim();
    const priceValue = manualPrice !== "" ? Number(manualPrice) : null;

    if (!url || !title || priceValue == null || Number.isNaN(priceValue) || priceValue <= 0) {
      setManualError("Introduce un enlace, un nombre y un precio válido.");
      return;
    }

    try {
      setSavingManual(true);
      await addManualFavorite(user.uid, { url, title, price: priceValue });
      setManualUrl("");
      setManualTitle("");
      setManualPrice("");
      await loadFavorites();
    } catch (error) {
      setManualError("No se ha podido guardar la vivienda. Inténtalo de nuevo.");
    } finally {
      setSavingManual(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando propiedades favoritas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 pt-20 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Propiedades Favoritas</h1>
              <p className="text-muted-foreground">{favorites.length} propiedades guardadas</p>
            </div>
          </div>
        </div>

        {/* Formulario para añadir vivienda manualmente */}
        <div className="mb-8 bg-card rounded-2xl p-4 shadow-lg">
          <h2 className="text-lg font-semibold mb-3">Añadir vivienda manualmente</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Pega el enlace de Idealista (u otra web), ponle un nombre y un precio aproximado. Calcularemos el score según tu perfil financiero.
          </p>
          <form onSubmit={handleAddManualFavorite} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Enlace de la vivienda</label>
              <input
                type="url"
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                placeholder="https://www.idealista.com/inmueble/..."
                className="w-full px-3 py-2 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Nombre</label>
              <input
                type="text"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                placeholder="Chalet en Santa Clara"
                className="w-full px-3 py-2 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Precio (€)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  placeholder="250000"
                  className="w-full px-3 py-2 rounded-lg border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <Button
                  type="submit"
                  variant="hero"
                  className="whitespace-nowrap"
                  disabled={savingManual}
                >
                  {savingManual ? "Guardando..." : "Añadir"}
                </Button>
              </div>
            </div>
          </form>
          {manualError && (
            <p className="mt-2 text-xs text-red-500">{manualError}</p>
          )}
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No tienes propiedades favoritas</h2>
            <p className="text-muted-foreground mb-6">Las propiedades que marques como favoritas aparecerán aquí</p>
            <Button onClick={() => navigate('/chat')} variant="hero">
              Buscar propiedades
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((favorite) => (
              <div key={favorite.id} className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                {/* Property Image */}
                <div className="relative h-64 bg-secondary">
                  {(() => {
                    // Buscar la imagen en diferentes campos posibles
                    const imageUrl = favorite.property_image || 
                                   favorite.thumbnail || 
                                   favorite.image || 
                                   favorite.images?.[0] || 
                                   favorite.photo || 
                                   favorite.photos?.[0] ||
                                   favorite.mainImage ||
                                   favorite.picture ||
                                   favorite.media?.[0] ||
                                   favorite.mediaUrl ||
                                   favorite.imageUrl ||
                                   favorite.url ||
                                   favorite.visual?.[0] ||
                                   favorite.visuals?.[0] ||
                                   favorite.mediaFiles?.[0] ||
                                   favorite.attachments?.[0];
                    
                    if (imageUrl) {
                      // Crear URL con proxy para evitar CORS
                      const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}`;
                      
                      return (
                        <img
                          src={proxyUrl}
                          alt={favorite.property_title || 'Propiedad'}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            const placeholder = e.target.parentElement.querySelector('.image-placeholder');
                            if (placeholder) {
                              placeholder.style.display = 'none';
                            }
                          }}
                        />
                      );
                    } else {
                      // Si no hay URL, mostrar placeholder
                      return (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-secondary to-secondary/50">
                          <div className="text-center">
                            <Square className="w-12 h-12 mx-auto mb-2" />
                            <p className="text-sm">Sin imagen</p>
                          </div>
                        </div>
                      );
                    }
                  })()}
                  
                  {/* Placeholder cuando no hay imagen o falla */}
                  <div className="image-placeholder absolute inset-0 w-full h-full flex items-center justify-center text-muted-foreground bg-gradient-to-br from-secondary to-secondary/50" style={{ display: 'none' }}>
                    <div className="text-center">
                      <Square className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">Sin imagen</p>
                    </div>
                  </div>
                  
                  {/* Remove from Favorites Button */}
                  <button
                    onClick={() => handleRemoveFavorite(favorite.property_code)}
                    className="absolute top-3 right-3 p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background transition-colors"
                    title="Eliminar de favoritos"
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>

                {/* Property Info */}
                <div className="p-6">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                    {favorite.property_title || 'Propiedad sin título'}
                  </h3>
                  
                  <div className="flex items-center gap-1 text-muted-foreground mb-3">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{favorite.property_location || 'Ubicación no disponible'}</span>
                  </div>

                  {/* Property Details */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
                    {(() => {
                      const rooms = favorite.property_rooms ?? favorite.property_details?.rooms;
                      return rooms ? (
                        <div className="flex items-center gap-1">
                          <Bed className="w-4 h-4" />
                          <span>{rooms} hab</span>
                        </div>
                      ) : null;
                    })()}
                    {(() => {
                      const bathrooms = favorite.property_bathrooms ?? favorite.property_details?.bathrooms;
                      return bathrooms ? (
                        <div className="flex items-center gap-1">
                          <Bath className="w-4 h-4" />
                          <span>{bathrooms} baños</span>
                        </div>
                      ) : null;
                    })()}
                    {(() => {
                      const size = favorite.property_size ?? favorite.property_details?.size;
                      return size ? (
                        <div className="flex items-center gap-1">
                          <Square className="w-4 h-4" />
                          <span>{size} m²</span>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Price + Housing Score */}
                  <div className="flex items-center justify-between mb-4 gap-2">
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(favorite.property_price)}
                    </span>
                    {favorite.property_housing_score != null && (
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                        Score vivienda: {Math.round(favorite.property_housing_score)}/100
                      </span>
                    )}
                  </div>

                  {/* Action Button */}
                  <Button
                    onClick={() => window.open(favorite.property_url, '_blank')}
                    variant="hero"
                    className="w-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ver en Idealista
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Favorites;
